import moment from 'moment';

import numeral from 'numeral';
import DataTable from '../../core/charts/Datatable';
import store from '../../../store';
import {
    OVERVIEW_PANEL_CHANGE_CURRENT,
    OVERVIEW_PANEL_GET_CURRENT,
    OVERVIEW_PANEL_IS_VISIBLE
} from '../../../store/modules/overview-panel/types';
import worldMap from '../../core/map';
import translate from '../../core/tools/translate';
import environment from '../../environment';
import CoreControl from "../CoreControl";
import VectorType from '../VectorType';
import filters from '../../core/filters';
import dataBoard from '../../core/databoard';

export default class CompanyCustomersPotentialAfterSalesControl extends CoreControl {
    constructor() {
        super({ name: "CompanyCustomersPotentialAfterSales" });

        this._change = this._change.bind(this);
        this._filterCallback = this._filterCallback.bind(this);
        this.createFilter = this.createFilter.bind(this);
        this._changePeriod = this._changePeriod.bind(this);
        this._getFilters = this._getFilters.bind(this);
        this.toggleOverview = this.toggleOverview.bind(this);
        this.updateFeatures = this.updateFeatures.bind(this);

        this.filterValues = {};
    }

    static getInstance() {
        if (!CompanyCustomersPotentialAfterSalesControl._instance) {
            CompanyCustomersPotentialAfterSalesControl._instance = new CompanyCustomersPotentialAfterSalesControl();
        }

        return CompanyCustomersPotentialAfterSalesControl._instance;
    }

    init() {
        this.colors = [
            "blue",
            "red",
            "green",
            "cyan",
            "pink",
            "orange",
            "lime",
            "yellow",
            "purple",
            "teal"
        ];

        const values = {
            help: translate(`Help.${this.options.name}`),
            helpInfo: translate(`Help.CompanyCustomersPotentialInfo`),
            id: "layer-company-customers-potential",
            input: "checkbox",
            label: translate(`Common.${this.options.name}`),
            layer: this.getLayer,
            filter: {
                enable: true,
                click: this.createFilter
            },
            name: this.options.name,
            type: "vector-points",
            change: this._change,
            overview: this.toggleOverview,
            links: {}
        };

        if (!this._instance) {
            this._instance = new VectorType(values);
        }
    }

    _change(e) {
        const layer = this.getLayer();

        if (e.checked) {
            layer.redraw();
            layer.reload();
        } else {
            dataBoard.hide();
            layer.hide();
            layer.clear();
        }

        this._setFilterButtonVisibility(e.checked);
    }

    _setFilterButtonVisibility(state) {
        this.getControls().button.overview.input.enable(state);
        this.getControls().button.overview.enable = state;

        if (
            store.getters[OVERVIEW_PANEL_GET_CURRENT] ===
            this.options.name &&
            !state
        ) {
            dataBoard.hide();
            delete this._overviewFeatures;
        }

        state ? this.createFilter() : filters.close();
    }

    createFilter() {
        filters.create({
            id: `${_.kebabCase(this.options.name)}-filter-id`,
            label: translate(`Common.${this.options.name}`),
            type: _.lowerCase(this.options.name),
            onSubmit: this._filterCallback,
        });
    }

    _filterCallback(values) {
        this.check(true);

        this.filterValues = { ...values };
        this.updateFeatures(this.filterValues);
    }

    _getFilters() {
        return [this.filterValues, this._date];
    }

    _changePeriod(date, id = this.getLayer().getMap()._id) {
        this._date.set(id, {
            start: moment(date.start),
            end: moment(date.end),
            min: new Date(date.min),
            max: new Date(date.max)
        });

        if (this._instance.check()) {
            this.add([...this.currentStores]);
        }
    }

    _getIconStyle(id) {
        const color = this.colors.shift();
        this.colors.push(color);
        return {
            store: L.icon({
                iconUrl: `${environment.get('IMAGES_HOST')}/pin_store_customers_${color}.png`,
                iconSize: [50, 64], // size of the icon
                iconAnchor: [25, 60], // point of the icon which will correspond to marker's location
                labelAnchor: [25, -32]
            }),
            customers: {
                value: id,
                style: {
                    color,
                    icon: {
                        iconUrl: `${environment.get('IMAGES_HOST')}/pin_circle_${color}_xs.png`,
                        iconSize: [12, 12],
                        iconAnchor: [9, 9]
                    },
                    clickable: false
                }
            }
        };
    }

    _setStoreIcon(featureCollection) {
        const layer = this.getLayer();
        const { symbology } = layer.options;

        const stores = _(featureCollection)
            .map(x => x.properties.ClientCompanyTypeName)
            .uniq()
            .value();

        stores.forEach(storeId => {
            const style = this._getIconStyle(storeId);

            symbology.values.push(style.customers);

            this.getLayer().setOptions({
                symbology
            });
        });
    }

    getDate(id = this.getLayer().getMap()._id) {
        const period = environment.get('CONFIG.FILTERS.CLIENT_COMPANY_PERIOD');

        if (_.isUndefined(this._date)) {
            this._date = new Map();
        }

        if (!this._date.has(id)) {

            if (id === 0)
                this._date.set(id, { start: moment(period.periodInitialFormatted, "YYYY-MM-DD"), end: moment(period.periodEndFormatted, "YYYY-MM-DD") });
            else
                this._date.set(id, this._date.get((id - 1)));
        }

        return this._date.get(id);
    }

    updateFeatures(filters) {
        const layer = this.getLayer();
        const clientCompanyTypeIds = filters.clientCompanyType.value.map(x => x);
        const typeSegmentMarketIds = filters.typeSegmentMarket.value.map(x => x);
        const socialCapitalTypeIds = filters.socialCapitalType.value.map(x => x);
        const date = this.getDate();

        layer.setOptions({
            remote: {
                params: {
                    ClientCompanyTypeIds: clientCompanyTypeIds,
                    TypeSegmentMarketIds: typeSegmentMarketIds,
                    SocialCapitalTypeIds: socialCapitalTypeIds,
                    YearInitial: date.start.format("YYYY"),
                    MonthInitial: date.start.format("MM"),
                    YearEnd: date.end.format("YYYY"),
                    MonthEnd: date.end.format("MM"),
                }
            }
        });

        if (this.check() && layer.visible()) {
            layer.clear();
            layer.redraw();
            layer.reload();
        }
    }

    toggleOverview() {
        const currentMap = worldMap.getCurrent();
        const layer = this.getLayer(currentMap._id);
        const features = layer.getCurrentFeatures();
        if (!store.getters[OVERVIEW_PANEL_IS_VISIBLE] || !_.isEqual(this._overviewFeatures, features))

            if ((_.size(features) && !_.isEqual(this._overviewFeatures, features)) || !store.getters[OVERVIEW_PANEL_IS_VISIBLE]) {
                this.summary(features);
                this._overviewFeatures = _.cloneDeep(features);

                store.dispatch(OVERVIEW_PANEL_CHANGE_CURRENT, this.options.name);
            }
    }

    summary(features) {
        const data = this.calculate(features);

        const chartPie = new DataTable(data.perCompanyType, {
            row: translate("Common.Name"),
            column: ["percentage"]
        });

        const chartColumn = new DataTable(data.perTier, {
            row: translate("Common.Name"),
            column: [translate("Common.Total")]
        });

        const chartColumnSecond = new DataTable(data.perSegment, {
            row: translate("Common.Name"),
            column: [translate("Common.Total")]
        });

        const nameKey = translate("Common.Name");
        const pieColors = _.map(data.perCompanyType, x => {
            const label = String(x[nameKey] || "").toLowerCase();
            if (label.indexOf("prospect") >= 0) return "#3498db";
            if (label.indexOf("inativ") >= 0) return "#e74c3c";
            if (label.indexOf("ativ") >= 0) return "#2ecc71";
            return "#95a5a6";
        });

        const options = {
            title: {
                table: `Tabela Segmento Mercado`,
                chartPie: "Clientes Ativos, Inativos e Prospects",
                chartColumn: "Capital Social",
                chartColumnSecond: "Quantidade por Segmento de Mercado"
            },
            dash: true,
            titleHeaderMessage: `Sumário - ${this.options.name}`,
            fourthLevel: true,
            dashTypes: { 3: 'column' },
            data: [
                data.segmentTable,
                chartPie,
                chartColumn,
                chartColumnSecond
            ],
            colors: [
                null,
                pieColors
            ],
            convertTable: false,
            format: ["{0:n0}", "{0:p2}", "{0:n0}", "{0:n0}"],
            columns: [
                {
                    field: "name",
                    title: "Segmento"
                },
                {
                    field: "total",
                    title: translate("Common.Total"),
                    width: 120,
                    template: r => numeral(r.total).format("0,0")
                },
                {
                    field: "percentage",
                    title: `${translate("Common.Total")} (%)`,
                    width: 100,
                    template: data =>
                        numeral(data.percentage).format("0,0[.]00%")
                }],
            buttons: { fullscreen: true, export: false }
        };

        dataBoard.show(options);
    }

    calculate(features) {
        const nameKey = translate("Common.Name");
        const totalKey = translate("Common.Total");

        const perCompanyType = _(features || [])
            .groupBy(f => (f && f.properties && f.properties.ClientCompanyTypeId) || 0)
            .map((vals) => {
                const p = vals[0] && vals[0].properties;
                const label = (p && p.ClientCompanyTypeName) ? p.ClientCompanyTypeName : "Não informado";
                const obj = {};
                obj[nameKey] = label;
                obj["percentage"] = vals.length / features.length;
                return obj;
            })
            .orderBy(totalKey).reverse()
            .value();

        const perTier = _(features || [])
            .groupBy(f => (f && f.properties && f.properties.SocialCapitalTypeId) || 0)
            .map((vals) => {
                const p = vals[0] && vals[0].properties;
                const label = (p && p.SocialCapitalTypeName) ? p.SocialCapitalTypeName : "Não informado";
                const obj = {};
                obj[nameKey] = label;
                obj[totalKey] = vals.length;
                return obj;
            })
            .orderBy(totalKey).reverse()
            .value();

        const perSegment = _(features || [])
            .groupBy(f => (f && f.properties && f.properties.TypeSegmentMarketId) || 0)
            .map((vals) => {
                const p = vals[0] && vals[0].properties;
                const label = (p && p.TypeSegmentMarketName) ? p.TypeSegmentMarketName : "Não informado";
                const obj = {};
                obj[nameKey] = label;
                obj[totalKey] = vals.length;
                return obj;
            })
            .orderBy(totalKey).reverse()
            .value();

        const segmentTable = _(features || [])
            .groupBy(f => (f && f.properties && f.properties.TypeSegmentMarketId) || 0)
            .map(vals => {
                const p = vals[0] && vals[0].properties;
                return {
                    name: (p && p.TypeSegmentMarketName) ? p.TypeSegmentMarketName : "Não informado",
                    total: vals.length
                };
            })
            .orderBy("total").reverse()
            .value();

        return {
            perCompanyType,
            perTier,
            perSegment,
            segmentTable,
        };
    }
}



// WEBPACK FOOTER //
// ./src/scripts/controls/AfterSales/CompanyCustomersPotentialAfterSalesControl.js